import infoStyles from "../components/builder/BuilderInfo.module.css";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api";
import { ButtonStatus, CourseType, FileType, TagType } from "../common/constants";
import Checkbox from "../components/Checkbox";
import Button from "../components/Button";
import PageBuilder from "../components/builder/PageBuilder";
import Select from "../components/Select";
import TagSelector from "../components/TagSelector";
import TextInput from "../components/TextInput";
import Uploader from "../components/Uploader";
import AlertDialog from "../components/dialogs/AlertDialog";
import IconsDialog from "../components/dialogs/IconsDialog";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import { DialogStatus } from "../enums";
import typo from "../typography.module.css";
import styles from "./CourseEditor.module.css";
import MultipleSelect from "../components/MultipleSelect";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { PageBuilderProvider } from "../components/builder/PageBuilderContext";
import { ReactComponent as CopyIcon } from '../assets/images/icons/ic-copy.svg';
import { ReactComponent as DeleteIcon } from '../assets/images/icons/ic-delete.svg';
import { ReactComponent as DocumentIcon } from '../assets/images/icons/ic-document-outlined.svg';
import TooltipWrapper from "../components/TooltipWrapper";

const euroToCents = (value) => {
    if (value === null || value === undefined || value === '') {
        return null
    }
    return Math.round(Number(value) * 100)
}

const getBrochureName = (url) => {
    const name = url.split(/[?#]/)[0].split('/').pop() || 'Brochure caricata'
    try {
        return decodeURIComponent(name)
    } catch (_) {
        return name
    }
}

const CourseEditor = () => {
    const { courseId } = useParams();
    const [course, setCourse] = useState(null)
    const [tags, setTags] = useState(null)
    const [status, setStatus] = useState(
        [
            { id: 0, name: 'Bozza', value: 'draft' },
            { id: 1, name: 'Pubblico', value: 'public' },
            { id: 2, name: 'Nascosto', value: 'hidden' }
        ]
    )
    const type = 
        [
            { id: 0, name: 'Masterclass', value: CourseType.Masterclass },
            { id: 1, name: 'Master', value: CourseType.Professional },
            { id: 2, name: 'Bootcamp', value: CourseType.Bootcamp },
            { id: 3, name: 'Webinar', value: CourseType.Webinar },
            { id: 4, name: 'Pack', value: CourseType.Pack },
        ]
    const [loading, setLoading] = useState(false)
    const [brochureUploading, setBrochureUploading] = useState(false)
    const [savingAction, setSavingAction] = useState(null)
    const [successAction, setSuccessAction] = useState(null)
  const [lastSavedAt, setLastSavedAt] = useState(null)
    const [alert, setAlert] = useState({ open: false, title: '', text: '', actions: [], status: DialogStatus.Default })
    const [infoToEdit, setInfoToEdit] = useState(null)
    const [uploaderReset, setUploaderReset] = useState(false)

    const [sections, setSections] = useState([])
    const [draftSections, setDraftSections] = useState([])
    const [builderVersion, setBuilderVersion] = useState('published')
    const [builderKey, setBuilderKey] = useState(0)

    const [singleCourses, setSingleCourses] = useState([])
    const [packCourses, setPackCourses] = useState([])

    const courseCategories = [
        {
            id: 0,
            name: 'Professionalizzanti',
            value: 'professional'
        },
        {
            id: 1,
            name: 'EduFin',
            value: 'edufin'
        },
        {
            id: 2,
            name: 'Webinar',
            value: 'webinar'
        },
        {
            id: 3,
            name: 'Corporate Academy',
            value: 'corporate'
        }

    ]

    const [selectedLists, setSelectedLists] = useState([]);
    const [lists, setLists] = useState([]);

    const removeNullProperties = (value) => {
        if (Array.isArray(value)) {
            return value.map(removeNullProperties)
        }

        if (value && typeof value === 'object') {
            return Object.entries(value).reduce((acc, [key, currentValue]) => {
                if (currentValue === null) {
                    return acc
                }

                acc[key] = removeNullProperties(currentValue)
                return acc
            }, {})
        }

        return value
    }

    useEffect(() => {

        const getSingleCourses = async () => {
            try {
                const courses = await api.get("/admin/courses")
                setSingleCourses(courses.filter(c => c.product?.type === 'single'))
            }
            catch (e) {
                console.error(e)
                return []
            }
        }

        const getCourse = async () => {
            try {
                const c = await api.get(`/admin/courses/${courseId}`)
                setCourse(c)
                getListsByIds(c.ac_lists ?? [])
                setSections(c.content || [])
                setDraftSections(c.draft || c.content || [])
                setBuilderVersion('published')
                setBuilderKey(prev => prev + 1)
                initializeStatus(c.status)
            }
            catch (e) {
                console.error(e)
            }
        }


        getSingleCourses()
        if (!course && courseId !== 'create') {
            getCourse()

        } else if (courseId === "create") {
            setCourse({
                name: "",
                description: "",
                status: "draft",
                type: CourseType.Masterclass,
                duration: "",
                modules: "",
                video_url: "",
                thumbnail: "",
                tags: [],
                faq: [
                    { "name": "Esempio domanda", "description": "Esempio risposta" }
                ],
                info: [
                    {
                        "icon": "<svg width=\"24\" height=\"24\" fill=\"none\" viewBox=\"0 0 24 24\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M3 5.75A.75.75 0 0 1 3.75 5c2.663 0 5.258-.943 7.8-2.85a.75.75 0 0 1 .9 0C14.992 4.057 17.587 5 20.25 5a.75.75 0 0 1 .75.75V11c0 .338-.014.67-.04.996a6.5 6.5 0 0 0-8.33 9.809l-.355.143a.75.75 0 0 1-.55 0C5.958 19.676 3 16 3 11V5.75ZM23 17.5a5.5 5.5 0 1 0-11 0 5.5 5.5 0 0 0 11 0Zm-2.146-2.354a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708l1.646 1.647 3.646-3.647a.5.5 0 0 1 .708 0Z\" fill=\"#212121\"/></svg>",
                        "color": "#1F94FF",
                        "index": 0,
                        "title": "Placeholder",
                        "chosen": false,
                        "iconName": "ic_fluent_shield_checkmark_24_filled.svg",
                        "selected": false,
                        "_selected": false,
                        "description": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed non ante faucibus, eleifend ligula tempor, luctus ligula. Praesent sem mauris, semper et aliquet id, fermentum eu lectus."
                    },
                ],
                program: [
                    {
                        "name": "Introduzione",
                        "chosen": false,
                        "selected": false,
                        "description": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed non ante faucibus, eleifend ligula tempor, luctus ligula. Praesent sem mauris, semper et aliquet id, fermentum eu lectus."
                    },
                ],
                product: {
                    name: "",
                    price: null,
                    discounted_price: null
                }
            })
        }
    }, [courseId])

    useEffect(() => {
        if (course && course.type === CourseType.Pack && singleCourses.length > 0 && packCourses.length === 0) {
            let pack = singleCourses?.filter(c => course.product?.components?.map(pc => pc.id).includes(c.product?.id))
            setPackCourses(pack)
        }
    }, [singleCourses, course])


    useEffect(() => {
        const getTags = async () => {
            try {
                let tags = await api.get("/admin/tags?type=all")
                tags = tags.filter(t => {
                    return !course.tags.map(t => t.id).includes(t.id)
                })
                setTags(tags)
            }
            catch (e) {
                console.error(e)
            }
        }
        if (!tags && course) {
            getTags()
        }
    }, [tags, course])

    const initializeStatus = (status) => {
        switch (status) {
            case 'draft':
                setStatus([
                    { id: 0, name: 'Bozza', value: 'draft' },
                    { id: 1, name: 'Pubblico', value: 'public' },
                ])
                break
            case 'public':
                setStatus([
                    { id: 1, name: 'Pubblico', value: 'public' },
                    { id: 2, name: 'Nascosto', value: 'hidden' }
                ])
                break
            case 'hidden':
                setStatus([
                    { id: 1, name: 'Pubblico', value: 'public' },
                    { id: 2, name: 'Nascosto', value: 'hidden' }
                ])
                break
            default: return
        }
    }

    // useEffect(() => {
    //     if (course) {
    //         switch (course.status) {
    //             case 'draft':
    //                 setSaveDisabled(course.name.trim() === '' || course.slug.trim() === '')
    //                 break
    //             case 'public':
    //             case 'hidden':
    //                 setSaveDisabled(
    //                     course.name.trim() === '' ||
    //                     course.description?.trim() === '' ||
    //                     course.slug.trim() === '' ||
    //                     course.thumbnail === undefined || course.thumbnail === null ||
    //                     course.thumbnail?.trim() === '' ||
    //                     course.duration === '' ||
    //                     course.modules === ''
    //                 )
    //                 break
    //             default: return
    //         }
    //     }
    // }, [course])

    const update = async (contentPage, draftPage, action = 'publish') => {
        setLoading(true)
        try {

            let body = {
                name: course.name,
                description: course.description,
                slug: course.slug,
                status: course.status,
                type: course.type,
                category: course.category,
                lessons: course.lessons,
                modules: course.modules,
                duration: course.duration,
                thumbnail: course.thumbnail,
                brochure: course.brochure || "",
                tags: course.tags.map(t => t.id),
                content: removeNullProperties(contentPage),
                draft: removeNullProperties(draftPage),
                requires_application: course.type === CourseType.Webinar ? true : course.requires_application,
                ac_lists: selectedLists.map(list => list.id),
                pack: course.type === CourseType.Pack ? packCourses.map(c => c.product.id) : null
            }

            if (course.product?.price !== null && course.product?.price !== undefined && course.product?.price !== '') {
                body.product = {
                    id: course.product?.id,
                    price: course.product?.price,
                    discounted_price: course.product?.discounted_price
                }
            }
            else {
                delete body.product
            }


            const updated = await api.put(`/admin/courses/${course.id}`, body)

            setCourse(updated)
            setSections(updated.content || [])
            setDraftSections(updated.draft || updated.content || [])
            initializeStatus(updated.status)
            setSuccessAction(action)
      setLastSavedAt(new Date().toISOString())

            setUploaderReset(!uploaderReset)
        }
        catch (e) {
            console.error(e)
            setAlert({
                open: true,
                title: 'Operazione Fallita',
                text: e.detail,
                status: DialogStatus.Error,
                actions: [
                    {
                        label: 'OK',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                        }
                    }
                ]
            })
        }
        setLoading(false)

    }

    const create = async (contentPage, draftPage, action = 'publish') => {
        setLoading(true)
        try {

            let body = {
                name: course.name,
                description: course.description,
                slug: course.slug,
                status: course.status,
                type: course.type,
                category: course.category,
                modules: course.modules,
                lessons: course.lessons,
                duration: course.duration,
                thumbnail: course.thumbnail,
                brochure: course.brochure || "",
                tags: course.tags.map(t => t.id),
                content: removeNullProperties(contentPage),
                draft: removeNullProperties(draftPage),
                requires_application: course.type === CourseType.Webinar ? true : course.requires_application,
                ac_lists: selectedLists.map(list => list.id),
                pack: course.type === CourseType.Pack ? packCourses.map(c => c.product.id) : null
            }

            if (course.product?.price !== null && course.product?.price !== undefined && course.product?.price !== '') {
                body.product = {
                    price: course.product?.price,
                    discounted_price: course.product?.discounted_price
                }
            }
            else {
                delete body.product
            }

            const created = await api.post(`/admin/courses`, body)

            setCourse(created)
            setSections(created.content || [])
            setDraftSections(created.draft || created.content || [])
            initializeStatus(created.status)
            setSuccessAction(action)
      setLastSavedAt(new Date().toISOString())

            setUploaderReset(!uploaderReset)

        }
        catch (e) {
            console.error(e)
            setAlert({
                open: true,
                title: 'Operazione Fallita',
                text: e.detail,
                status: DialogStatus.Error,
                actions: [
                    {
                        label: 'OK',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                        }
                    }
                ]
            })
        }
        setLoading(false)
    }

    const save = async (page, activeVersion = 'published', action = 'publish') => {
        let nextPublishedPage = sections
        let nextDraftPage = draftSections

        if (activeVersion === 'draft') {
            if (action === 'save_draft') {
                nextDraftPage = page
            } else {
                nextPublishedPage = page
                nextDraftPage = page
            }
        } else {
            nextPublishedPage = page
            if (action === 'export_to_draft') {
                nextDraftPage = page
            }
        }

        setSavingAction(action)
        try {
            if (course.id) {
                await update(nextPublishedPage, nextDraftPage, action)
            }
            else {
                await create(nextPublishedPage, nextDraftPage, action)
            }
        }
        finally {
            setSavingAction(null)
        }
    }

    const getSaveDisabled = useMemo(() => {
        if (!course || brochureUploading) {
            return true
        }

        if (course.type === CourseType.Pack) {
            return packCourses.length < 2 || !packCourses || loading || savingAction !== null
        }

        if (course.status === 'draft') {
            return course.name.trim() === '' || course.slug.trim() === '' || loading || savingAction !== null
        }

        if (course.status === 'public') {
            return course.name.trim() === '' ||
                course.description?.trim() === '' ||
                course.slug.trim() === '' ||
                course.thumbnail === undefined || course.thumbnail === null ||
                course.thumbnail?.trim() === '' ||
                loading ||
                savingAction !== null
        }
    }, [course, packCourses, loading, savingAction, brochureUploading])

    const searchLists = async (value) => {
        try {
            const lists = await api.get(`/admin/active-campaign/lists?q=${encodeURIComponent(value)}`);
            // sostituisci tutte le liste tranne quelle selezionate
            let newLists = lists.filter(list => !selectedLists.map(sl => parseInt(sl.id)).includes(parseInt(list.id)));
            setLists([...selectedLists, ...newLists]);
        }
        catch (e) {
            console.error("Error fetching lists:", e);
        }
    }

    const getListsByIds = async (ids) => {
        try {
            const lists = await api.get(`/admin/active-campaign/lists?ids=${encodeURIComponent(ids.join(','))}`);
            setLists(lists);
            setSelectedLists(lists.map(list => ({ id: parseInt(list.id), name: list.name })));
        }
        catch (e) {
            console.error("Error fetching lists by ids:", e);
        }
    }

    const getCoursePathByCategory = (category) => {
        switch (category) {
            case 'professional':
                return 'courses'
            case 'edufin':
                return 'financial-education'
            case 'webinar':
                return 'webinar'
            case 'corporate':
                return 'corporate-academy'
            case 'bootcamp':
                return 'bootcamp'
            default:
                return 'courses'
        }
    }

    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (copied) {
            setTimeout(() => {
                setCopied(false);
            }, 1000);
        }
    }, [copied]);

    useEffect(() => {
        if (!successAction) {
            return
        }

        const timeout = setTimeout(() => {
            setSuccessAction(null)
        }, 2000)

        return () => clearTimeout(timeout)
    }, [successAction])

    const editBar =
        <div className={infoStyles.edit}>
            {course &&
                <div className={infoStyles.card}>
                    <a rel="noreferrer" target="_blank" href={`/shop_courses/${course.id}/preview`} className={infoStyles.previewLink}>APRI PREVIEW</a>
                    <div className={infoStyles.publicUrl}>{process.env.REACT_APP_WEBAPP_URL + `/${getCoursePathByCategory(course?.category)}/${course?.slug}`}
                        <TooltipWrapper text={copied ? "Copiato!" : "Copia"}>
                            <CopyIcon className={infoStyles.copyIcon}
                                onClick={() => {
                                    navigator.clipboard.writeText(`${process.env.REACT_APP_WEBAPP_URL}/${getCoursePathByCategory(course?.category)}/${course?.slug}`)
                                    setCopied(true);
                                }} />
                        </TooltipWrapper>
                    </div>
                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>STATO</div>
                        <Select appearance="modern" selected={status.find(s => s.value === course?.status)} options={status} onSelect={(value) => {
                            setCourse((prev) => {
                                prev.status = value.value
                                return { ...prev }
                            })
                        }} />
                    </div>
                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>TIPOLOGIA</div>
                        <Select appearance="modern" selected={type.find(s => s.value === course?.type)} options={type} onSelect={(value) => {
                            setCourse((prev) => {
                                prev.type = value.value
                                return { ...prev }
                            })
                        }} />
                    </div>
                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>CATEGORIA</div>
                        <Select appearance="modern" selected={courseCategories.find(s => s.value === course?.category)} options={courseCategories} onSelect={(value) => {
                            setCourse((prev) => {
                                prev.category = value.value
                                return { ...prev }
                            })
                        }} />
                    </div>
                    {course.type === CourseType.Pack &&
                        <div className={infoStyles.editSection}>
                            <div className={infoStyles.label}>PRODOTTI PACK</div>
                            <MultipleSelect appearance="modern" placeholder="Seleziona prodotti..." selected={packCourses} options={singleCourses.sort((a, b) => a.name.localeCompare(b.name))} onSelect={(selected) => setPackCourses(selected)} />
                        </div>
                    }
                    <div className={infoStyles.editSection}>

                        <div className={infoStyles.label}>NOME</div>
                        <TextInput appearance="modern"
                            style={{ fontSize: '.75rem' }}
                            type="text"
                            value={course?.name}
                            placeholder="Nome"
                            error={course?.name.trim() === ''}
                            errorMessage="Campo obbligatorio"
                            onKeyUp={(value) => {
                                setCourse((prev) => {
                                    prev.name = value
                                    if (prev.status === 'draft') {
                                        prev.slug = value?.replaceAll('-', '').replace(/[^a-zA-Z0-9\s-]/g, '') // Rimuovi tutti i caratteri tranne lettere e numeri
                                            .replace(/\s+/g, '-') // Sostituisci spazi con trattini
                                            .toLowerCase()
                                            .trim();
                                    }
                                    return { ...prev }
                                })
                            }} />
                    </div>

                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>SLUG</div>
                        <TextInput appearance="modern"
                            style={{ fontSize: '.75rem' }}
                            type="text" value={course?.slug}
                            error={(course?.slug ?? '').trim() === ''}
                            errorMessage="Campo obbligatorio"
                            placeholder="Slug"
                            onKeyUp={(value) => {
                                const newValue = value?.replace(/[^a-zA-Z0-9\s-]/g, '') // Rimuovi tutti i caratteri tranne lettere e numeri
                                    .replace(/\s+/g, '-') // Sostituisci spazi con trattini
                                    .toLowerCase()
                                    .trim(); // Converti tutto in minuscolo
                                setCourse((prev) => {
                                    prev.slug = newValue
                                    return { ...prev }
                                })
                            }} />
                    </div>

                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>DESCRIZIONE</div>
                        <textarea className={infoStyles.description} value={course?.description} onChange={(e) => {
                            setCourse((prev) => {
                                prev.description = e.target.value
                                return { ...prev }
                            })
                        }} />
                    </div>

                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>ACTIVE CAMPAIGN LISTS</div>
                        {/* <TextInput appearance="modern"
                            style={{ fontSize: '.75rem' }}
                            type="text" 
                            value={course?.ac_lists}
                            placeholder="100, 101, 102"
                            onKeyUp={(value) => {
                                setCourse((prev) => {
                                    prev.ac_lists = value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
                                    return { ...prev }
                                })
                            }} /> */}
                        <MultipleSelect appearance="modern"
                            selected={lists?.filter(list => selectedLists.map(sl => parseInt(sl.id)).includes(parseInt(list.id)))}
                            onSelect={(items) => {
                                setSelectedLists(items.map(i => ({ id: parseInt(i.id), name: i.name })));
                            }}
                            searchPlaceholder="Cerca per nome"
                            onSearch={searchLists}
                            options={lists} />
                    </div>

                    {course.type !== CourseType.Webinar &&
                        <>
                            <div className={infoStyles.editSection}>
                                <div className={infoStyles.label}>CANDIDATURA</div>
                                <Checkbox value={course?.requires_application} label="Richiede Candidatura" labelPosition="end" onChange={(value) => {
                                    setCourse((prev) => {
                                        prev.requires_application = value
                                        return { ...prev }
                                    })
                                }} />
                                <div className={infoStyles.label}>Attivando questa spunta sarà necessario creare una candidatura per il corso nella sezione 'Candidature'.</div>
                            </div>


                            <div className={infoStyles.editSection}>
                                <div className={infoStyles.label}>DURATA(H)</div>
                                <TextInput appearance="modern"
                                    style={{ fontSize: '.75rem' }}
                                    type="number" value={course?.duration ?? '0'}
                                    placeholder="Durata"
                                    error={course?.status !== 'draft' && (course?.duration === '' || course?.duration <= 0)}
                                    errorMessage="Campo obbligatorio"
                                    onKeyUp={(value) => {
                                        setCourse((prev) => {
                                            prev.duration = value
                                            return { ...prev }
                                        })
                                    }} />
                            </div>

                            <div className={infoStyles.editSection}>
                                <div className={infoStyles.label}>MODULI</div>
                                <TextInput appearance="modern"
                                    style={{ fontSize: '.75rem' }}
                                    type="number" value={course?.modules}
                                    error={course?.status !== 'draft' && (course?.modules === '' || course?.modules <= 0)}
                                    errorMessage="Campo obbligatorio"
                                    placeholder="Moduli"
                                    onKeyUp={(value) => {
                                        setCourse((prev) => {
                                            prev.modules = value
                                            return { ...prev }
                                        })
                                    }} />
                            </div>

                            <div className={infoStyles.editSection}>
                                <div className={infoStyles.label}>PREZZO(€)</div>
                                <TextInput appearance="modern"
                                    style={{ fontSize: '.75rem' }}
                                    type="number"
                                    numberType="float"
                                    error={course?.product?.price === null || course?.product?.price === ''}
                                    errorMessage="Campo obbligatorio"
                                    value={course?.product?.price != null && course.product.price !== '' ? course.product.price / 100 : ''}
                                    placeholder="Prezzo"
                                    onKeyUp={(value) => {
                                        const price = euroToCents(value)
                                        setCourse((prev) => {
                                            if (prev.product) {
                                                prev.product.price = price
                                            }
                                            else {
                                                prev.product = {
                                                    name: "",
                                                    price: price,
                                                    discounted_price: null
                                                }
                                            }
                                            return { ...prev }
                                        })
                                    }} />
                            </div>

                            <div className={infoStyles.editSection}>
                                <div className={infoStyles.label}>PREZZO SCONTATO(€)</div>
                                <TextInput appearance="modern"
                                    style={{ fontSize: '.75rem' }}
                                    type="number"
                                    numberType="float"
                                    error={course?.product?.discounted_price > course?.product?.price}
                                    errorMessage="Il prezzo scontato non può essere maggiore del prezzo pieno"
                                    value={course?.product?.discounted_price != null && course.product.discounted_price !== '' ? course.product.discounted_price / 100 : ''}
                                    placeholder="Prezzo scontato"
                                    onKeyUp={(value) => {
                                        const discountedPrice = euroToCents(value)
                                        setCourse((prev) => {
                                            if (prev.product) {
                                                prev.product.discounted_price = discountedPrice
                                            }
                                            else {
                                                prev.product = {
                                                    name: "",
                                                    price: prev.product.price,
                                                    discounted_price: discountedPrice
                                                }
                                            }
                                            return { ...prev }
                                        })
                                    }} />
                            </div>
                        </>
                    }

                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>THUMBNAIL</div>
                        <TextInput appearance="modern"
                            style={{ fontSize: '.75rem' }}
                            disabled={course?.thumbnail?.startsWith('blob:')}
                            type="text" value={course?.thumbnail}
                            error={course?.status !== 'draft' && (course?.thumbnail === undefined || course?.thumbnail === null || course?.thumbnail?.trim() === '')}
                            errorMessage="Campo obbligatorio"
                            placeholder="Thumbnail URL"
                            onKeyUp={(value) => {
                                setCourse((prev) => {
                                    prev.thumbnail = value ? value : ''
                                    return { ...prev }
                                })
                            }} />

                        <Uploader
                            crop={course.type !== CourseType.Bootcamp}
                            aspect={course.type !== CourseType.Bootcamp ? 16 / 9 : null}
                            shape={'rect'}
                            preview={course.thumbnail}
                            reset={uploaderReset}
                            multiple={false}
                            type={FileType.Image}
                            iconButtons={true}
                            onComplete={(uploads) => {
                                setCourse((prev) => {
                                    prev.thumbnail = uploads[0].url
                                    return { ...prev }
                                })
                            }}
                            onDelete={() => {
                                setCourse((prev) => {
                                    prev.thumbnail = ""
                                    return { ...prev }
                                })
                            }}
                        />
                    </div>

                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>BROCHURE</div>
                        <div className={infoStyles.label}>Scaricabile dal pulsante “Scarica brochure” dopo l’invio della candidatura. Configura una candidatura pubblica e non scaduta per il corso.</div>
                        {course.brochure ? (
                            <div className={styles.brochureFile}>
                                <a className={styles.brochureLink} href={course.brochure} target="_blank" rel="noopener noreferrer">
                                    <span className={styles.brochureIcon}><DocumentIcon aria-hidden="true" /></span>
                                    <span className={styles.brochureDetails}>
                                        <span className={styles.brochureName}>{getBrochureName(course.brochure)}</span>
                                        <span className={styles.brochureHint}>Apri brochure ↗</span>
                                    </span>
                                </a>
                                <Button
                                    type="button"
                                    appearance="subtle"
                                    additionalClass={styles.removeBrochure}
                                    aria-label="Rimuovi brochure"
                                    title="Rimuovi brochure"
                                    requiresEdit
                                    disabled={brochureUploading}
                                    onClick={() => setCourse(prev => ({ ...prev, brochure: '' }))}
                                >
                                    <DeleteIcon aria-hidden="true" />
                                </Button>
                            </div>
                        ) : (
                            <Uploader
                                label="CARICA BROCHURE"
                                message="Carica il file della brochure del corso"
                                type={FileType.All}
                                multiple={false}
                                reset={uploaderReset}
                                subdirectory="courses/brochures"
                                onStart={() => setBrochureUploading(true)}
                                onProgress={(_, uploads) => setBrochureUploading(uploads.some(upload => !['completed', 'failed'].includes(upload.status)))}
                                onComplete={(uploads) => {
                                    setCourse(prev => ({ ...prev, brochure: uploads[0].url }))
                                    setBrochureUploading(false)
                                }}
                            />
                        )}
                    </div>

                    {tags &&
                        <>

                            <div className={infoStyles.editSection}>
                                <div className={infoStyles.label}>TAG</div>
                                <TagSelector
                                    selected={course.tags.filter(t => t.type === TagType.General)}
                                    options={tags.filter(t => t.type === TagType.General)}
                                    placeholder={"Cerca tag"}
                                    onAdd={(item) => {
                                        const newOptions = tags.filter(t => t.id !== item.id)
                                        setTags([...newOptions])

                                        setCourse((prev) => {
                                            if (!prev.tags.map(t => t.id).includes(item.id)) {
                                                prev.tags.push(item)
                                            }
                                            return { ...prev }
                                        })
                                    }}
                                    onRemove={(item) => {
                                        const newOptions = [...tags]
                                        if (!newOptions.map(t => t.id).includes(item.id)) {
                                            newOptions.push(item)
                                        }
                                        setTags([...newOptions])

                                        setCourse((prev) => {
                                            prev.tags = prev.tags.filter(t => t.id !== item.id)
                                            return { ...prev }
                                        })
                                    }}
                                />
                            </div>
                            {/* <div className={infoStyles.editSection}>
                                <div className={infoStyles.label}>FEATURES TAG</div>
                                <TagSelector
                                    selected={course.tags.filter(t => t.type === TagType.Feature)}
                                    options={tags.filter(t => t.type === TagType.Feature)}
                                    placeholder={"Cerca tag"}
                                    onAdd={(item) => {
                                        const newOptions = tags.filter(t => t.id !== item.id)
                                        setTags([...newOptions])

                                        setCourse((prev) => {
                                            if (!prev.tags.map(t => t.id).includes(item.id)) {
                                                prev.tags.push(item)
                                            }
                                            return { ...prev }
                                        })
                                    }}
                                    onRemove={(item) => {
                                        const newOptions = [...tags]
                                        if (!newOptions.map(t => t.id).includes(item.id)) {
                                            newOptions.push(item)
                                        }
                                        setTags([...newOptions])

                                        setCourse((prev) => {
                                            prev.tags = prev.tags.filter(t => t.id !== item.id)
                                            return { ...prev }
                                        })
                                    }}
                                />
                            </div> */}
                        </>
                    }
                </div>
            }
        </div >

    return (
        <HeaderFooterLayout hideHeader hideFooter>
            <HelmetProvider>
                <Helmet>
                    <title>{course ? `${course.name} - Editor` : "Corso - Editor"}</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.preview}>
                    {course &&
                        <PageBuilderProvider
                            key={`${course.id || 'create'}-${builderVersion}-${builderKey}`}
                            initialPage={sections}
                            draft={draftSections}
                            initialVersion={builderVersion}
                        >
                            <PageBuilder
                                element={course}
                lastSavedAt={lastSavedAt || course?.updated_at || course?.created_at}
                                customTab={
                                    {
                                        name: "Info",
                                        content: editBar,
                                        saveDisabled: getSaveDisabled
                                    }
                                }
                                onSave={(page, activeVersion, action) => save(page, activeVersion, action)}
                                isSaving={savingAction !== null}
                                publishButtonStatus={
                                    savingAction === 'publish'
                                        ? ButtonStatus.Loading
                                        : successAction === 'publish'
                                            ? ButtonStatus.Success
                                            : null
                                }
                                saveDraftButtonStatus={
                                    savingAction === 'save_draft'
                                        ? ButtonStatus.Loading
                                        : successAction === 'save_draft'
                                            ? ButtonStatus.Success
                                            : null
                                }
                                onVersionChange={(nextVersion) => {
                                    setBuilderVersion(nextVersion)
                                    setBuilderKey(prev => prev + 1)
                                }} />
                        </PageBuilderProvider>
                    }
                </div>

            </div>
            <AlertDialog
                open={alert.open}
                title={alert.title}
                html={alert.text}
                actions={alert.actions}
                status={alert.status}
                onClose={() => {
                    setAlert((prev) => {
                        prev.open = false
                        return { ...prev }
                    })
                }
                } />

            {infoToEdit &&
                <IconsDialog open={infoToEdit !== null}
                    selected={infoToEdit?.iconName}
                    onClose={() => {
                        setInfoToEdit(null)
                    }}
                    onSelect={async (icon) => {
                        const data = icon.data
                        let info = course.info.map((info, index) => {
                            if (index === infoToEdit.index) {
                                info.icon = data
                                info.iconName = icon.svgFileName
                            }
                            return info
                        })
                        setCourse((prev) => {
                            prev.info = info
                            return { ...prev }
                        })
                        setInfoToEdit(null)
                    }} />
            }
        </HeaderFooterLayout>
    )
}

export default CourseEditor
